import { clsx } from "clsx";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

export interface SelectOption {
  value: string;
  label: string;
}

export type SelectSize = "sm" | "md";

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  size?: SelectSize;
}

export function Select({
  options,
  value,
  onChange,
  label,
  placeholder = "Wybierz...",
  error,
  hint,
  disabled = false,
  className,
  id: externalId,
  size = "md",
}: SelectProps) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const listboxId = `${id}-listbox`;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [flipUp, setFlipUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Determine if dropdown should flip upward (before paint to avoid flicker)
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) {
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownMaxHeight = 240; // max-h-60 = 15rem = 240px
    setFlipUp(spaceBelow < dropdownMaxHeight && rect.top > spaceBelow);
  }, [isOpen]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) {
      return;
    }
    const list = listRef.current;
    const item = list?.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightedIndex]);

  function toggle() {
    if (disabled) {
      return;
    }
    setIsOpen((prev) => {
      if (!prev) {
        const idx = options.findIndex((o) => o.value === value);
        setHighlightedIndex(idx >= 0 ? idx : 0);
      }
      return !prev;
    });
  }

  function selectOption(optionValue: string) {
    onChange(optionValue);
    setIsOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (disabled) {
      return;
    }

    switch (e.key) {
      case "Enter":
      case " ": {
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          selectOption(options[highlightedIndex]?.value ?? "");
        } else {
          toggle();
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setIsOpen(false);
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          const idx = options.findIndex((o) => o.value === value);
          setHighlightedIndex(idx >= 0 ? idx : 0);
        } else {
          setHighlightedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : prev,
          );
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;
      }
      case "Tab": {
        setIsOpen(false);
        break;
      }
    }
  }

  return (
    <div
      className={clsx("flex flex-col gap-1.5", className)}
      ref={containerRef}
    >
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-sub">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          ref={buttonRef}
          id={id}
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={
            isOpen && highlightedIndex >= 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error && errorId, hint && hintId].filter(Boolean).join(" ") ||
            undefined
          }
          disabled={disabled}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          className={clsx(
            "flex w-full items-center justify-between border bg-panel text-left",
            "transition-colors duration-150",
            "focus:outline-none focus:ring-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            size === "sm"
              ? "h-8 gap-1 rounded-md px-2.5 text-[13px]"
              : "h-9 gap-2 rounded-md px-3 text-sm",
            error
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-edge hover:border-edge-hard focus:border-adara-500 focus:ring-adara-500",
          )}
        >
          <span
            className={clsx(
              "truncate whitespace-nowrap",
              selectedOption ? "text-main" : "text-faint",
            )}
          >
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronDown
            size={size === "sm" ? 14 : 16}
            aria-hidden="true"
            className={clsx(
              "shrink-0 text-faint transition-transform duration-200",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen && (
          <div
            id={listboxId}
            role="listbox"
            ref={listRef}
            className={clsx(
              "absolute z-50 max-h-60 w-full min-w-max overflow-auto rounded-md border border-edge bg-panel py-1 shadow-lg",
              flipUp ? "bottom-full mb-1" : "top-full mt-1",
            )}
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={option.value}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={isSelected}
                  onMouseEnter={() => {
                    setHighlightedIndex(index);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      selectOption(option.value);
                    }
                  }}
                  onClick={() => {
                    selectOption(option.value);
                  }}
                  className={clsx(
                    "flex cursor-pointer items-center justify-between",
                    "transition-colors duration-75",
                    size === "sm" ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm",
                    isHighlighted && "bg-adara-50 dark:bg-adara-900/30",
                    isSelected &&
                      "font-medium text-adara-700 dark:text-adara-300",
                    !isSelected && "text-main",
                  )}
                >
                  {option.label}
                  {isSelected && (
                    <Check
                      size={size === "sm" ? 14 : 16}
                      aria-hidden="true"
                      className="shrink-0 text-adara-700 dark:text-adara-300"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={hintId} className="text-sm text-dim">
          {hint}
        </p>
      )}
    </div>
  );
}
